import React, { useEffect, useRef, useState } from 'react';
import { CheckSquare, Download, Plus, Save, Settings2, Trash2, Upload, X } from 'lucide-react';

const CHECKLIST_ITEM_TYPES = [
  { type: 'value', label: 'Value' },
  { type: 'dropdown', label: 'Dropdown' },
  { type: 'checkbox', label: 'Check' },
  { type: 'longtext', label: 'Long Text' },
  { type: 'document', label: 'Document' }
];

const createChecklistItem = (type) => ({
  id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  type,
  options: type === 'dropdown' ? ['Option 1', 'Option 2'] : []
});

const normalizeItems = (items) => Array.isArray(items) ? items.map((item) => ({
  id: String(item?.id || `item_${Math.random().toString(36).slice(2, 8)}`),
  label: String(item?.label || ''),
  type: String(item?.type || 'value'),
  options: Array.isArray(item?.options) ? item.options.map((option) => String(option || '')) : []
})) : [];

const normalizeValues = (values) => (values && typeof values === 'object' ? { ...values } : {});

const normalizePresetItems = (items) => normalizeItems(items);

const normalizeDocumentValue = (value) => {
  const documentName = String(value?.documentName || '').trim();
  const documentPath = String(value?.documentPath || '').trim();
  if (!documentName || !documentPath) {
    return null;
  }

  return { documentName, documentPath };
};

const normalizeDocumentValues = (value) => {
  const documents = Array.isArray(value) ? value : [value];
  return documents.map((documentValue) => normalizeDocumentValue(documentValue)).filter(Boolean);
};

const areItemsEquivalent = (leftItems, rightItems) => {
  const left = normalizePresetItems(leftItems);
  const right = normalizePresetItems(rightItems);

  if (left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => {
    const candidate = right[index];
    if (!candidate) {
      return false;
    }

    return item.id === candidate.id
      && item.label === candidate.label
      && item.type === candidate.type
      && JSON.stringify(item.options || []) === JSON.stringify(candidate.options || []);
  });
};

const UniversalChecklistModal = ({
  isOpen,
  title,
  typeLabel,
  items,
  values,
  loading = false,
  saving = false,
  onClose,
  onSaveValues,
  onSaveTemplate,
  onUploadDocument,
  onDownloadDocument,
  documentAccept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar',
  templatePresets = [],
  renderCustomField,
  allowMultipleDocuments,
  templateEditingEnabled = true
}) => {
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const [draftValues, setDraftValues] = useState({});
  const [uploadingDocumentItemId, setUploadingDocumentItemId] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const wasOpenRef = useRef(false);
  const didInitializeRef = useRef(false);

  const activePreset = templatePresets.find((preset) => preset.id === selectedPresetId) || null;
  const isLockedPreset = Boolean(activePreset?.locked);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      didInitializeRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      didInitializeRef.current = false;
    }

    if (!loading && !didInitializeRef.current) {
      setDraftItems(normalizeItems(items));
      setDraftValues(normalizeValues(values));
      setIsEditingTemplate(false);
      const matchedPreset = templatePresets.find((preset) => areItemsEquivalent(items, preset.items));
      setSelectedPresetId(matchedPreset?.id || '');
      didInitializeRef.current = true;
    }

    wasOpenRef.current = true;
  }, [isOpen, items, loading, templatePresets, values]);

  if (!isOpen) {
    return null;
  }

  const handleItemChange = (itemId, patch) => {
    setSelectedPresetId('');
    setDraftItems((currentItems) => currentItems.map((item) => item.id === itemId ? { ...item, ...patch } : item));
  };

  const handleValueChange = (item, value) => {
    setDraftValues((currentValues) => ({
      ...currentValues,
      [item.id]: item.type === 'checkbox' ? Boolean(value) : value
    }));
  };

  const handleDocumentUpload = async (item, file) => {
    if (!item || !file || !onUploadDocument) {
      return;
    }

    setUploadingDocumentItemId(item.id);
    try {
      const nextValue = await onUploadDocument(item, file);
      if (nextValue) {
        handleValueChange(item, nextValue);
      }
    } finally {
      setUploadingDocumentItemId(null);
    }
  };

  const handleDeleteItem = (itemId) => {
    setSelectedPresetId('');
    setDraftItems((currentItems) => currentItems.filter((item) => item.id !== itemId));
    setDraftValues((currentValues) => {
      const nextValues = { ...currentValues };
      delete nextValues[itemId];
      return nextValues;
    });
  };

  const handleDropdownOptionChange = (itemId, optionIndex, nextValue) => {
    setSelectedPresetId('');
    setDraftItems((currentItems) => currentItems.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      const nextOptions = Array.isArray(item.options) ? [...item.options] : [];
      nextOptions[optionIndex] = nextValue;
      return { ...item, options: nextOptions };
    }));
  };

  const handleAddDropdownOption = (itemId) => {
    setSelectedPresetId('');
    setDraftItems((currentItems) => currentItems.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      return {
        ...item,
        options: [...(Array.isArray(item.options) ? item.options : []), '']
      };
    }));
  };

  const handleDeleteDropdownOption = (itemId, optionIndex) => {
    setSelectedPresetId('');
    setDraftItems((currentItems) => currentItems.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      const nextOptions = (Array.isArray(item.options) ? item.options : []).filter((_, index) => index !== optionIndex);
      return {
        ...item,
        options: nextOptions.length > 0 ? nextOptions : ['']
      };
    }));
  };

  const renderChecklistField = (item) => {
    const customField = renderCustomField?.({
      item,
      value: draftValues[item.id],
      values: draftValues,
      setValue: (nextValue) => handleValueChange(item, nextValue)
    });

    if (customField) {
      return customField;
    }

    const commonStyle = {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: '1px solid #dbe3ee',
      fontSize: '14px',
      color: '#334155',
      boxSizing: 'border-box'
    };

    if (item.type === 'checkbox') {
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#0f172a', fontWeight: '500' }}>
          <input
            type="checkbox"
            checked={Boolean(draftValues[item.id])}
            onChange={(event) => handleValueChange(item, event.target.checked)}
          />
          Mark as done
        </label>
      );
    }

    if (item.type === 'dropdown') {
      return (
        <select
          value={String(draftValues[item.id] || '')}
          onChange={(event) => handleValueChange(item, event.target.value)}
          style={commonStyle}
        >
          <option value="">Select an option</option>
          {(item.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (item.type === 'longtext') {
      return (
        <textarea
          value={String(draftValues[item.id] || '')}
          onChange={(event) => handleValueChange(item, event.target.value)}
          style={{ ...commonStyle, minHeight: '110px', resize: 'vertical' }}
          placeholder="Enter details"
        />
      );
    }

    if (item.type === 'document') {
      const allowsMultipleDocuments = allowMultipleDocuments?.(item) === true;
      const documentValues = allowsMultipleDocuments
        ? normalizeDocumentValues(draftValues[item.id])
        : (() => {
            const documentValue = normalizeDocumentValue(draftValues[item.id]);
            return documentValue ? [documentValue] : [];
          })();
      const hasDocument = documentValues.length > 0;
      const isUploading = uploadingDocumentItemId === item.id;

      return (
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept={documentAccept}
              multiple={allowsMultipleDocuments}
              disabled={!onUploadDocument || isUploading}
              onChange={async (event) => {
                const files = Array.from(event.target.files || []);
                if (files.length === 0) {
                  event.target.value = '';
                  return;
                }

                if (allowsMultipleDocuments) {
                  setUploadingDocumentItemId(item.id);
                  try {
                    const uploadedDocuments = [];
                    for (const file of files) {
                      const nextValue = await onUploadDocument?.(item, file);
                      const nextDocument = normalizeDocumentValue(nextValue);
                      if (nextDocument) {
                        uploadedDocuments.push(nextDocument);
                      }
                    }

                    if (uploadedDocuments.length > 0) {
                      setDraftValues((currentValues) => ({
                        ...currentValues,
                        [item.id]: [...normalizeDocumentValues(currentValues[item.id]), ...uploadedDocuments]
                      }));
                    }
                  } finally {
                    setUploadingDocumentItemId(null);
                  }
                } else if (files[0]) {
                  await handleDocumentUpload(item, files[0]);
                }
                event.target.value = '';
              }}
              style={{ ...commonStyle, flex: '1 1 240px', backgroundColor: '#ffffff' }}
            />
            {isUploading ? <span style={{ color: '#8b5cf6', fontSize: '13px', fontWeight: '600' }}>Uploading...</span> : null}
          </div>

          {hasDocument ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              {documentValues.map((documentValue, documentIndex) => (
                <div key={`${item.id}-document-${documentValue.documentPath}-${documentIndex}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', padding: '10px 12px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #dbe3ee', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                    <div style={{ color: '#0f172a', fontWeight: '600', wordBreak: 'break-word' }}>{documentValue.documentName}</div>
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>{allowsMultipleDocuments ? `Uploaded document ${documentIndex + 1}` : 'Uploaded document'}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => onDownloadDocument?.(documentValue, item)}
                      style={secondaryButtonStyle}
                    >
                      <Download size={16} /> Download
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (allowsMultipleDocuments) {
                          setDraftValues((currentValues) => ({
                            ...currentValues,
                            [item.id]: normalizeDocumentValues(currentValues[item.id]).filter((_, index) => index !== documentIndex)
                          }));
                          return;
                        }

                        handleValueChange(item, null);
                      }}
                      style={dangerButtonStyle}
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '13px' }}>No document uploaded yet.</div>
          )}
        </div>
      );
    }

    return (
      <input
        type="text"
        value={String(draftValues[item.id] || '')}
        onChange={(event) => handleValueChange(item, event.target.value)}
        style={commonStyle}
        placeholder="Enter value"
      />
    );
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Checklist</div>
            <h3 style={{ margin: '6px 0 0', color: '#0f172a' }}>{title}</h3>
            {typeLabel ? <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Type: {typeLabel}</div> : null}
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {templateEditingEnabled && onSaveTemplate ? (
              <button type="button" onClick={() => setIsEditingTemplate((value) => !value)} style={secondaryButtonStyle}>
                <Settings2 size={16} /> {isEditingTemplate ? 'Back To Checklist' : 'Edit Template'}
              </button>
            ) : null}
            <button type="button" onClick={onClose} style={iconButtonStyle}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={bodyStyle}>
          {loading ? (
            <div style={emptyStateStyle}>Loading checklist...</div>
          ) : isEditingTemplate ? (
            <div style={{ display: 'grid', gap: '16px' }}>
              {templatePresets.length > 0 ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  <div style={{ color: '#475569', fontSize: '13px', fontWeight: '700' }}>Hardcoded templates</div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {templatePresets.map((preset) => {
                      const isActive = selectedPresetId === preset.id;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => {
                            setSelectedPresetId(preset.id);
                            setDraftItems(normalizeItems(preset.items));
                          }}
                          style={{
                            ...secondaryButtonStyle,
                            backgroundColor: isActive ? '#fff7ed' : '#ffffff',
                            borderColor: isActive ? '#fdba74' : '#dbe3ee',
                            color: isActive ? '#9a3412' : '#334155'
                          }}
                        >
                          <Settings2 size={16} /> {preset.label}
                        </button>
                      );
                    })}
                  </div>
                  {activePreset?.description ? (
                    <div style={{ color: '#64748b', fontSize: '13px' }}>{activePreset.description}</div>
                  ) : null}
                </div>
              ) : null}

              {isLockedPreset ? (
                <div style={{ padding: '12px 14px', borderRadius: '10px', backgroundColor: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', fontSize: '13px', fontWeight: '600' }}>
                  This template is hardcoded and cannot be edited inside the app.
                </div>
              ) : null}

              {!isLockedPreset ? (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {CHECKLIST_ITEM_TYPES.map((itemType) => (
                    <button
                      key={itemType.type}
                      onClick={() => {
                        setSelectedPresetId('');
                        setDraftItems((currentItems) => [...currentItems, createChecklistItem(itemType.type)]);
                      }}
                      style={secondaryButtonStyle}
                    >
                      {itemType.type === 'document' ? <Upload size={16} /> : <Plus size={16} />} Add {itemType.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {draftItems.length === 0 ? (
                <div style={emptyStateStyle}>No checklist items yet. Add the fields you want for this type.</div>
              ) : draftItems.map((item, index) => (
                <div key={item.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ color: '#0f172a', fontWeight: '700' }}>Item {index + 1}</div>
                    {!isLockedPreset ? (
                      <button onClick={() => handleDeleteItem(item.id)} style={dangerButtonStyle}>
                        <Trash2 size={14} /> Delete
                      </button>
                    ) : null}
                  </div>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(event) => handleItemChange(item.id, { label: event.target.value })}
                      readOnly={isLockedPreset}
                      style={fieldStyle}
                      placeholder="Checklist item label"
                    />
                    <select
                      value={item.type}
                      onChange={(event) => handleItemChange(item.id, {
                        type: event.target.value,
                        options: event.target.value === 'dropdown' ? (item.options?.length ? item.options : ['Option 1']) : []
                      })}
                      disabled={isLockedPreset}
                      style={fieldStyle}
                    >
                      {CHECKLIST_ITEM_TYPES.map((itemType) => (
                        <option key={itemType.type} value={itemType.type}>{itemType.label}</option>
                      ))}
                    </select>
                    {item.type === 'dropdown' && !isLockedPreset ? (
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {(item.options || ['']).map((option, optionIndex) => (
                          <div key={`${item.id}-option-${optionIndex}`} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={option}
                              onChange={(event) => handleDropdownOptionChange(item.id, optionIndex, event.target.value)}
                              style={{ ...fieldStyle, flex: 1 }}
                              placeholder={`Dropdown option ${optionIndex + 1}`}
                            />
                            <button
                              onClick={() => handleDeleteDropdownOption(item.id, optionIndex)}
                              style={dangerButtonStyle}
                              type="button"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => handleAddDropdownOption(item.id)}
                          style={secondaryButtonStyle}
                          type="button"
                        >
                          <Plus size={16} /> Add option
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : draftItems.length === 0 ? (
            <div style={emptyStateStyle}>No checklist configured for this type yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {draftItems.map((item) => (
                <div key={item.id} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <CheckSquare size={16} color="#d97706" />
                    <div style={{ color: '#0f172a', fontWeight: '700' }}>{item.label}</div>
                  </div>
                  {renderChecklistField(item)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <button onClick={onClose} style={secondaryButtonStyle}>Close</button>
          {isEditingTemplate && templateEditingEnabled && onSaveTemplate ? (
            <button type="button" onClick={() => onSaveTemplate?.(draftItems)} disabled={saving} style={primaryButtonStyle}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save Template'}
            </button>
          ) : (
            <button type="button" onClick={() => onSaveValues?.(draftValues)} disabled={saving || draftItems.length === 0} style={primaryButtonStyle}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save Checklist'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  zIndex: 1200
};

const modalStyle = {
  width: 'min(860px, 100%)',
  maxHeight: '90vh',
  backgroundColor: '#ffffff',
  borderRadius: '18px',
  boxShadow: '0 30px 80px rgba(15, 23, 42, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const headerStyle = {
  padding: '20px 24px',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px'
};

const bodyStyle = {
  padding: '20px 24px',
  overflowY: 'auto'
};

const footerStyle = {
  padding: '18px 24px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px'
};

const cardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '16px',
  backgroundColor: '#f8fafc'
};

const fieldStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #dbe3ee',
  fontSize: '14px',
  color: '#334155',
  boxSizing: 'border-box'
};

const emptyStateStyle = {
  border: '1px dashed #cbd5e1',
  borderRadius: '12px',
  padding: '24px',
  color: '#64748b',
  textAlign: 'center'
};

const primaryButtonStyle = {
  padding: '10px 16px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: '#d97706',
  color: '#ffffff',
  cursor: 'pointer',
  fontWeight: '700',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px'
};

const secondaryButtonStyle = {
  padding: '10px 14px',
  borderRadius: '10px',
  border: '1px solid #dbe3ee',
  backgroundColor: '#ffffff',
  color: '#334155',
  cursor: 'pointer',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px'
};

const dangerButtonStyle = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#fee2e2',
  color: '#b91c1c',
  cursor: 'pointer',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
};

const iconButtonStyle = {
  width: '38px',
  height: '38px',
  borderRadius: '999px',
  border: '1px solid #dbe3ee',
  backgroundColor: '#ffffff',
  color: '#334155',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default UniversalChecklistModal;